import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

// SRC Import
import TEXT from "../../../src/utils/globalText";

// REDUX Imports
import {
  setMultipleEntity,
  setShowReloginModalFalse,
} from "../../redux/features/login/login";
import {
  xpentraLogout,
  xpentraLogoutSliceReset,
} from "../../redux/features/logout/logout";
import { logout } from "../../redux/features/refreshToken/refreshToken";
import ICONS from "../../assets/Icons";
import useInitials from "../../utils/useInitials.js";
import SVGComponent from "../../components/_utils/SVGComponent";

const ProfileQuickMenu = ({
  openProfile = false,
  setOpenProfile = () => {},
}) => {
  // useNavigate
  const navigate = useNavigate();

  // useDispatch
  const dispatch = useDispatch();

  const initials = useInitials();;

  // useLocation
  const location = useLocation();

  // useState
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 800);

  // useSelector
  const data = useSelector((state) => state.refreshToken.data);
  const ENTITY_NAME_LENGTH_THRESHOLD = 18;
  const { xpentraLogoutLoading, xpentraLogoutData, xpentraLogoutError } =
    useSelector((state) => state?.xpentraLogoutSlice);

  // useEffect
  useEffect(() => {
    if (
      !xpentraLogoutLoading &&
      !xpentraLogoutData &&
      xpentraLogoutError?.isErrorStatus
    ) {
      setOpenProfile(false);

      dispatch(xpentraLogoutSliceReset());
    }
  }, [
    xpentraLogoutLoading,
    xpentraLogoutData,
    xpentraLogoutError,
    dispatch,
    navigate,
    setOpenProfile,
  ]);

  useEffect(() => {
    const handleResize = () => {
      const isMobileNow = window.innerWidth <= 800;

      if (isMobileNow && openProfile) {
        navigate("/home/profile_quick_menu");
        if (typeof setOpenProfile === "function") {
          setOpenProfile(false);
        }
      }

      if (!isMobileNow && location.pathname === "/home/profile_quick_menu") {
        navigate("/home");
        if (typeof setOpenProfile === "function") {
          setOpenProfile(true);
        }
      }
      setIsMobile(isMobileNow);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [openProfile, navigate, setOpenProfile, location.pathname]);

  return (
    <>
      {/* Responsive header */}
      <div className="Qsmd:hidden w-width-full top-top-base bg-primary-nav z-z-index-header flex items-center">
        <span
          className="m-margin-button-l cursor-pointer"
          onClick={() => navigate("/home")}
        >
          <SVGComponent
            url={ICONS?.ARROW_LEFT_WHITE}
            alt="back"
            color="var(--text-primary)"
            width="var(--icon-sm)"
            height="var(--icon-sm)"
          />
        </span>
        <p className="text-font-md text-text-primary font-normal">
          {TEXT?.PROFILE}
        </p>
      </div>
      {/* Component content */}
      <div
        className="h-auto w-width-full Qsmd:w-profile-card-component-width"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className="flex flex-col gap-y-gap-sm justify-center items-center py-padding-md">
          <span className="bg-primary-nav-act h-10 w-10 Qmd:w-[var(--profile-initials-w-h)] Qmd:h-[var(--profile-initials-w-h)] rounded-full flex items-center justify-center text-text-primary font-semibold text-xs">
            {initials}
          </span>
          <div className="flex flex-col gap-gap-sm Qmd:gap-y-gap-xs w-width-full px-padding-md">
            <p className="text-font-xs text-text-primary text-center font-normal">
              {data?.employees?.user_id || "User ID"}
            </p>
            <p
              className={`text-font-sm text-text-primary text-center font-semibold capitalize truncate`}
            >
              {data?.employees?.first_name?.toLowerCase() +
                " " +
                (data?.employees?.last_name?.toLowerCase()
                  ? data?.employees?.last_name.toLowerCase()
                  : "") || "User Name"}
            </p>
          </div>
        </div>

        {/* Entity ID + Name */}
        <div className="w-width-full flex justify-center items-center py-padding-xs px-padding-md bg-primary-nav">
          <p className="text-font-xs text-text-primary font-normal truncate">
            {(data?.user?.user_id || data?.entity?.entity_id || "Entity ID") +
              " | "}
            {data?.entity?.entity_name?.length > ENTITY_NAME_LENGTH_THRESHOLD
              ? data?.entity?.entity_name || "Entity Name"
              : data?.entity?.entity_name || "Entity Name"}
          </p>
        </div>

        <div className="w-width-full bg-primary-nav-act flex flex-row">
          {/* MY PROFILE */}
          <div
            onClick={() => {
              navigate("/my_profile");
              setOpenProfile(false);
            }}
            className="relative w-width-full py-padding-sm flex flex-col gap-y-gap-xs justify-center items-center cursor-pointer focus:outline-none global-hover-white z-z-index-base"
          >
            <SVGComponent
              url={ICONS?.USER_IN_CIRCLE}
              color={"var(--text-primary)"}
              width="var(--icon-lg)"
              height="var(--icon-lg)"
              alt="My Profile"
            />
            <p className="text-font-xxs text-text-primary text-center font-normal">
              {TEXT?.MY_PROFILE}
            </p>
          </div>
          <div className="border-r border-solid border-primary-bg"></div>

          {/* SETTINGS */}
          <div
            onClick={() => {
              navigate("/settings");
              setOpenProfile(false);
            }}
            className="relative w-width-full py-padding-sm flex flex-col gap-y-gap-xs justify-center items-center cursor-pointer focus:outline-none global-hover-white z-z-index-base"
          >
            <SVGComponent
              url={ICONS?.SETTINGS_IN_CIRCLE}
              color={"var(--text-primary)"}
              width="var(--icon-lg)"
              height="var(--icon-lg)"
              alt="Settings"
            />
            <p className="text-font-xxs text-text-primary text-center font-normal">
              {TEXT?.SETTINGS}
            </p>
          </div>
          <div className="border-r border-solid border-primary-bg"></div>

          {/* LOGOUT */}
          <div
            onClick={() => {
              navigate("/login");
              dispatch(xpentraLogout());
              dispatch(setMultipleEntity());
              dispatch(setShowReloginModalFalse());
              dispatch(logout());
              dispatch(xpentraLogoutSliceReset());
              sessionStorage.clear();
            }}
            className="relative w-width-full py-padding-sm flex flex-col gap-y-gap-xs justify-center items-center cursor-pointer focus:outline-none global-hover-white z-z-index-base"
          >
            <SVGComponent
              url={ICONS?.LOGOUT_IN_WHITE_CIRCLE}
              color={"var(--text-primary)"}
              width="var(--icon-lg)"
              height="var(--icon-lg)"
              alt="Logout"
            />
            <p className="text-font-xxs text-text-primary text-center font-normal">
              {TEXT?.LOGOUT}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfileQuickMenu;
